import { useEffect, useState } from 'preact/hooks';
import { formatIngredient, NUTRIENTS, readNutrition, withoutNutritionSection, type IngredientGroup, type IngredientItem, type Nutrition, type RecipeStep, type UnitMode } from '../../shared/recipe';
import { ErrorBox, Loading, Modal, Segmented, Sheet, StarInput, Stars, minutes, typeLabel } from '../components/ui';
import { api, ApiError, type Card, type CardType } from '../lib/api';
import { renderMarkdown } from '../lib/markdown';
import { Link, navigate } from '../lib/router';
import { errorText, prefs, setPref, toast, useAsync, useIsAdmin } from '../lib/store';
import { shuffle } from './Home';

// ---------------------------------------------------------------- recipe pieces (also used by the sub-recipe sheet and Cook mode)

export function useScale(card: Card | undefined) {
  const base = card?.data?.servings?.amount ?? 1;
  const [servings, setServings] = useState<number>(base);
  useEffect(() => setServings(base), [card?.id, base]);
  return { servings, setServings, factor: base ? servings / base : 1, base };
}

export function ServingsStepper({ value, onChange, unit }: { value: number; onChange: (n: number) => void; unit: string }) {
  const step = value <= 1 ? 0.5 : 1;
  return (
    <div class="row">
      <div class="stepper" role="group" aria-label="Servings">
        <button type="button" aria-label="Fewer" onClick={() => onChange(Math.max(0.5, value - step))}>
          –
        </button>
        <input
          aria-label={`Number of ${unit}`}
          inputMode="decimal"
          value={String(value)}
          onChange={(e) => {
            const n = Number(e.currentTarget.value.replace(',', '.'));
            if (n > 0 && n < 10000) onChange(n);
          }}
        />
        <button type="button" aria-label="More" onClick={() => onChange(value + (value < 1 ? 0.5 : 1))}>
          +
        </button>
      </div>
      <span class="muted">{unit}</span>
    </div>
  );
}

export function UnitToggle() {
  const p = prefs.use();
  return (
    <Segmented<UnitMode>
      label="Units"
      value={p.units}
      onChange={(v) => setPref('units', v)}
      options={[
        ['as_written', 'As written'],
        ['metric', 'Metric'],
        ['us', 'US'],
      ]}
    />
  );
}

export function IngredientList({
  groups,
  factor,
  checkable,
  onOpenLinked,
}: {
  groups: IngredientGroup[];
  factor: number;
  checkable?: boolean;
  onOpenLinked?: (id: string) => void;
}) {
  const units = prefs.use().units;
  const [checked, setChecked] = useState<Set<string>>(new Set());
  return (
    <>
      {groups.map((g, gi) => (
        <div>
          {g.name && <h3>{g.name}</h3>}
          <ul class="ing-list">
            {g.items.map((it: IngredientItem, ii) => {
              const k = `${gi}.${ii}`;
              const isChecked = checked.has(k);
              const text = formatIngredient(it, factor, units);
              return (
                <li class={isChecked ? 'checked' : ''}>
                  {checkable && (
                    <input
                      class="dr-check"
                      type="checkbox"
                      aria-label={`Got ${it.item}`}
                      checked={isChecked}
                      onChange={() =>
                        setChecked((s) => {
                          const n = new Set(s);
                          if (n.has(k)) n.delete(k);
                          else n.add(k);
                          return n;
                        })
                      }
                    />
                  )}
                  <span class="ing-text" title={it.raw && it.raw !== text ? `As written: ${it.raw}` : undefined}>
                    {it.linked_card_id && onOpenLinked ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onOpenLinked(it.linked_card_id!);
                        }}
                      >
                        {text}
                      </a>
                    ) : (
                      text
                    )}
                    {!it.scalable && factor !== 1 && it.qty !== null && <span class="not-scaled">not scaled</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}

export function StepList({ steps }: { steps: RecipeStep[] }) {
  return (
    <ol class="steps">
      {steps.map((s) => (
        <li>
          <div>
            <p>{s.text}</p>
            {s.timer_min ? <span class="muted small">⏱ {minutes(s.timer_min)}</span> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function SubRecipeSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, error } = useAsync(() => api.get<Card>(`/cards/${id}`), [id]);
  const { factor } = useScale(data);
  return (
    <Sheet title={data?.title ?? 'Sub-recipe'} onClose={onClose}>
      {error ? <ErrorBox error={error} /> : !data ? <Loading /> : (
        <>
          <p class="muted">{data.summary}</p>
          <h3>Ingredients</h3>
          <IngredientList groups={data.data.ingredient_groups ?? []} factor={factor} />
          <h3>Method</h3>
          <StepList steps={data.data.steps ?? []} />
          <Link class="dr-btn" href={`/c/${data.id}/${data.slug}`} onClick={onClose}>
            Open full recipe
          </Link>
        </>
      )}
    </Sheet>
  );
}

function CookedDialog({ card, onClose, onSaved }: { card: Card; onClose: () => void; onSaved: (c: Card) => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const save = async (e: Event) => {
    e.preventDefault();
    try {
      const r = await api.post<{ card: Card }>(`/cards/${card.id}/cooked`, { date, note: note || null, rating }, { 'idempotency-key': crypto.randomUUID() });
      toast('Logged — enjoy!');
      onSaved(r.card);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  };
  return (
    <Modal title="I baked this" onClose={onClose}>
      <form onSubmit={save}>
        <div class="field">
          <label for="cd">Date</label>
          <input id="cd" class="dr-input" type="date" value={date} onInput={(e) => setDate(e.currentTarget.value)} required />
        </div>
        <div class="field">
          <label>How was it?</label>
          <StarInput value={rating} onChange={setRating} />
        </div>
        <div class="field">
          <label for="cn">Note</label>
          <textarea id="cn" class="dr-input" placeholder="Needed more lemon…" value={note} onInput={(e) => setNote(e.currentTarget.value)} />
        </div>
        <button class="dr-btn dr-btn--primary">Save</button>
      </form>
    </Modal>
  );
}

function CollectionDialog({ card, onClose }: { card: Card; onClose: () => void }) {
  const { data, reload } = useAsync(() => api.get<{ items: { id: string; name: string; count: number }[] }>('/collections'), []);
  const [name, setName] = useState('');
  const inSet = new Set((card.collections ?? []).map((c) => c.id));
  const add = async (id: string) => {
    await api.post(`/collections/${id}/cards`, { card_id: card.id });
    toast('Added to collection');
    onClose();
  };
  const create = async (e: Event) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post('/collections', { name, card_ids: [card.id] });
    toast(`Created “${name}”`);
    reload();
    onClose();
  };
  return (
    <Modal title="Add to collection" onClose={onClose}>
      <div class="stack">
        {data?.items.map((c) => (
          <button class="dr-btn" disabled={inSet.has(c.id)} onClick={() => add(c.id)}>
            {c.name} {inSet.has(c.id) ? '✓' : ''}
          </button>
        ))}
        <form class="row" onSubmit={create}>
          <input class="dr-input grow" placeholder="New collection…" aria-label="New collection name" value={name} onInput={(e) => setName(e.currentTarget.value)} />
          <button class="dr-btn dr-btn--primary">Create</button>
        </form>
      </div>
    </Modal>
  );
}

const nutrientValue = (n: number, unit: string) => (unit === 'kcal' || n >= 100 ? Math.round(n) : Math.round(n * 10) / 10).toLocaleString('en-GB');

/** "rolls" → "roll", "servings" → "serving"; a unit that isn't a simple plural stays as it is. */
const singular = (unit: string) => (/[^s]s$/.test(unit) ? unit.slice(0, -1) : unit);

/**
 * Estimated nutrition: per 100 g, per portion (when the recipe makes several) and for the whole
 * recipe, which follows the servings stepper.
 */
function NutritionPanel({ n, servings, factor }: { n: Nutrition; servings: { amount: number; unit: string } | null; factor: number }) {
  const rows = NUTRIENTS.filter(([k]) => n.per_100g[k] !== undefined || n.whole_recipe[k] !== undefined);
  const hasWhole = rows.some(([k]) => n.whole_recipe[k] !== undefined);
  const perPortion = hasWhole && servings && servings.amount > 1 ? servings : null;
  const cell = (v: number | undefined, unit: string, times = 1) => (v === undefined ? '–' : nutrientValue(v * times, unit));
  return (
    <section class="section nutrition-block" aria-labelledby="nutrition-h">
      <div class="row-between">
        <h2 id="nutrition-h">Nutrition</h2>
        <span class="dr-label">Estimated</span>
      </div>
      <div class="nutrition-wrap">
        <table class="nutrition">
          <thead>
            <tr>
              <th scope="col">Nutrient</th>
              <th scope="col">Per 100&nbsp;g</th>
              {perPortion && <th scope="col">Per {singular(perPortion.unit)}</th>}
              {hasWhole && <th scope="col">Whole recipe{factor !== 1 ? ' (scaled)' : ''}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, label, unit]) => (
              <tr key={k} class={label.startsWith('of which') ? 'sub' : undefined}>
                <th scope="row">
                  {label} <span class="muted">({unit})</span>
                </th>
                <td>{cell(n.per_100g[k], unit)}</td>
                {perPortion && <td>{cell(n.whole_recipe[k], unit, 1 / perPortion.amount)}</td>}
                {hasWhole && <td>{cell(n.whole_recipe[k], unit, factor)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {n.estimated_baked_weight_g && (
        <p class="muted small">Estimated baked weight: {nutrientValue(n.estimated_baked_weight_g * factor, 'g')} g{factor !== 1 ? ' (scaled)' : ''}</p>
      )}
      <p class="muted small">{n.note ?? 'Approximate, ingredient-based values; actual values vary with baked weight, brands and optional ingredients.'}</p>
    </section>
  );
}

function RecipeBody({ card, onOpenLinked }: { card: Card; onOpenLinked: (id: string) => void }) {
  const { servings, setServings, factor } = useScale(card);
  const [tab, setTab] = useState<'ingredients' | 'method'>('ingredients');
  const d = card.data;
  const nutrition = readNutrition(d.nutrition, card.body_md);
  return (
    <>
      <div class="toolbar">
        {d.servings && <ServingsStepper value={servings} onChange={setServings} unit={d.servings.unit} />}
        <UnitToggle />
      </div>
      <div class="mobile-tabs">
        <Segmented
          label="Section"
          value={tab}
          onChange={setTab}
          options={[
            ['ingredients', 'Ingredients'],
            ['method', 'Method'],
          ]}
        />
      </div>
      <div class="recipe-cols" data-tab={tab}>
        <section class="ingredients" aria-labelledby="ing-h">
          <h2 id="ing-h">Ingredients</h2>
          <IngredientList groups={d.ingredient_groups ?? []} factor={factor} checkable onOpenLinked={onOpenLinked} />
          {d.equipment?.length ? <p class="muted small">Equipment: {d.equipment.join(', ')}</p> : null}
        </section>
        <section class="method" aria-labelledby="method-h">
          <h2 id="method-h">Method</h2>
          <StepList steps={d.steps ?? []} />
          {d.yield_note && <p class="notice">{d.yield_note}</p>}
        </section>
      </div>
      {nutrition && <NutritionPanel n={nutrition} servings={d.servings ?? null} factor={factor} />}
    </>
  );
}

/** Generic renderer for non-recipe types: payload as a labelled field list driven by the schema. */
function GenericBody({ card, type }: { card: Card; type?: CardType }) {
  const props = type?.json_schema?.properties ?? {};
  const entries = Object.entries(card.data ?? {}).filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && !v.length));
  if (!entries.length) return null;
  return (
    <dl class="panel">
      {entries.map(([k, v]) => (
        <>
          <dt class="muted small">{props[k]?.title ?? k.replace(/_/g, ' ')}</dt>
          <dd style={{ margin: '0 0 var(--bk-space-3)' }}>{Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
        </>
      ))}
    </dl>
  );
}

function useJsonLd(card: Card | undefined) {
  useEffect(() => {
    if (!card || card.type !== 'recipe') return;
    const d = card.data;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      name: card.title,
      description: card.summary ?? undefined,
      image: card.cover ? [new URL(card.cover.url, location.origin).toString()] : undefined,
      recipeYield: d.servings ? `${d.servings.amount} ${d.servings.unit}` : undefined,
      prepTime: d.times?.prep_min ? `PT${d.times.prep_min}M` : undefined,
      cookTime: d.times?.cook_min ? `PT${d.times.cook_min}M` : undefined,
      totalTime: d.times?.total_min ? `PT${d.times.total_min}M` : undefined,
      recipeCuisine: d.cuisine?.join(', ') || undefined,
      recipeCategory: d.course?.join(', ') || undefined,
      keywords: card.tags.join(', ') || undefined,
      recipeIngredient: (d.ingredient_groups ?? []).flatMap((g: IngredientGroup) => g.items.map((i) => formatIngredient(i))),
      recipeInstructions: (d.steps ?? []).map((s: RecipeStep) => ({ '@type': 'HowToStep', text: s.text })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify(ld).replace(/</g, '\\u003c');
    document.head.appendChild(el);
    return () => el.remove();
  }, [card?.id, card?.version]);
}

export function CardView({ id }: { id: string }) {
  const { data: card, error, loading, setData } = useAsync(() => api.get<Card>(`/cards/${id}`), [id]);
  const types = useAsync(() => api.get<{ items: CardType[] }>('/types'), []);
  const [linked, setLinked] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'cooked' | 'collection' | null>(null);
  const admin = useIsAdmin();
  useJsonLd(card);
  useEffect(() => {
    if (card) document.title = `${card.title} · Bakery`;
    return () => void (document.title = 'Bakery');
  }, [card?.title]);

  if (loading && !card) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const c = card!;

  const patch = async (body: Record<string, unknown>, msg: string) => {
    try {
      const r = await api.patch<{ card: Card }>(`/cards/${c.id}`, { ...body, change_note: msg }, { 'if-match': `"${c.version}"` });
      setData({ ...r.card, collections: c.collections });
    } catch (e) {
      if (e instanceof ApiError && e.status === 412) {
        toast('This card changed elsewhere — reloaded the latest version.', 'error');
        setData(await api.get<Card>(`/cards/${c.id}`));
      } else toast(errorText(e), 'error');
    }
  };
  const action = async (fn: () => Promise<unknown>, msg: string, after?: () => void) => {
    try {
      await fn();
      toast(msg);
      if (after) after();
      else setData(await api.get<Card>(`/cards/${c.id}`));
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const d = c.data;
  const cooked = (d.cooked_log ?? []) as { date: string; note?: string; rating?: number }[];
  // a recipe's nutrition table moves out of the notes into its own section
  const notes = c.body_md && c.type === 'recipe' && readNutrition(d.nutrition, c.body_md) ? withoutNutritionSection(c.body_md) : c.body_md;

  return (
    <article lang="en">
      {admin && c.needs_review && (
        <div class="notice notice-warning row-between" role="status" style={{ marginBottom: 'var(--bk-space-4)' }}>
          <span>
            <strong>Needs review.</strong> Last changed by {c.updated_by === 'user' ? 'you' : c.updated_by.replace('client:', '')} — check the{' '}
            <Link href={`/c/${c.id}/history`}>history</Link>.
          </span>
          <button class="dr-btn bk-sm" onClick={() => action(() => api.post(`/cards/${c.id}/reviewed`), 'Marked reviewed')}>
            Mark reviewed
          </button>
        </div>
      )}
      <header class="detail-header">
        <div class="detail-cover">{c.cover ? <img src={c.cover.url} alt={c.cover.alt_text ?? c.title} /> : <span class="dr-plate">{typeLabel(c.type)}</span>}</div>
        <div class="stack">
          <div class="row">
            {c.status === 'draft' && <span class="dr-tag bk-draft">Draft</span>}
            {c.status === 'archived' && <span class="dr-tag bk-archived">Archived</span>}
            <span class="muted small">{types.data?.items.find((t) => t.key === c.type)?.label ?? c.type}</span>
          </div>
          <h1>{c.title}</h1>
          {c.summary && <p class="muted">{c.summary}</p>}
          <div class="row">
            <Stars value={c.rating} />
            {admin ? (
              <button class="dr-btn bk-ghost bk-sm" aria-pressed={c.favourite} onClick={() => patch({ favourite: !c.favourite }, c.favourite ? 'Unfavourited' : 'Favourited')}>
                {c.favourite ? '★ Favourite' : '☆ Favourite'}
              </button>
            ) : (
              c.favourite && <span class="dr-tag">Favourite</span>
            )}
          </div>
          {c.type === 'recipe' && (
            <dl class="dr-specs">
              {d.times?.total_min ? (
                <div>
                  <dt>Total</dt>
                  <dd>{minutes(d.times.total_min)}</dd>
                </div>
              ) : null}
              {d.times?.prep_min ? (
                <div>
                  <dt>Prep</dt>
                  <dd>{minutes(d.times.prep_min)}</dd>
                </div>
              ) : null}
              {d.times?.cook_min ? (
                <div>
                  <dt>Bake</dt>
                  <dd>{minutes(d.times.cook_min)}</dd>
                </div>
              ) : null}
              {d.servings && (
                <div>
                  <dt>Makes</dt>
                  <dd>
                    {d.servings.amount} {d.servings.unit}
                  </dd>
                </div>
              )}
              {d.difficulty && (
                <div>
                  <dt>Difficulty</dt>
                  <dd>{d.difficulty}</dd>
                </div>
              )}
            </dl>
          )}
          {c.tags.length > 0 && (
            <div class="chips" aria-label="Tags">
              {c.tags.map((t) => (
                <Link class="dr-tag" href={`/browse?tag=${encodeURIComponent(t)}`}>
                  {t}
                </Link>
              ))}
            </div>
          )}
          {(c.source_url || c.source_note) && (
            <p class="small muted">
              Source:{' '}
              {c.source_url ? (
                <a href={c.source_url} rel="noopener noreferrer nofollow" target="_blank">
                  {c.source_note || new URL(c.source_url).hostname}
                </a>
              ) : (
                c.source_note
              )}
            </p>
          )}
          <div class="toolbar">
            {c.type === 'recipe' && (
              <Link class="dr-btn dr-btn--primary" href={`/c/${c.id}/bake`}>
                Bake mode
              </Link>
            )}
{admin && (
<>
            <Link class="dr-btn" href={`/c/${c.id}/edit`}>
              Edit
            </Link>
            {c.type === 'recipe' && (
              <button class="dr-btn" onClick={() => setDialog('cooked')}>
                I baked this
              </button>
            )}
            <button class="dr-btn" onClick={() => setDialog('collection')}>
              Collect
            </button>
            <Link class="dr-btn bk-ghost" href={`/c/${c.id}/history`}>
              History · v{c.version}
            </Link>
            {c.status === 'archived' ? (
              <button class="dr-btn bk-ghost" onClick={() => action(() => api.post(`/cards/${c.id}/unarchive`), 'Unarchived')}>
                Unarchive
              </button>
            ) : (
              <button class="dr-btn bk-ghost" onClick={() => action(() => api.post(`/cards/${c.id}/archive`, { change_note: 'Archived' }), 'Archived')}>
                Archive
              </button>
            )}
            <button
              class="dr-btn bk-danger"
              onClick={() =>
                confirm(`Move “${c.title}” to Trash?`) && action(() => api.del(`/cards/${c.id}`), 'Moved to Trash', () => navigate('/'))
              }
            >
              Delete
            </button>
</>
)}
          </div>
        </div>
      </header>

      {c.type === 'recipe' ? <RecipeBody card={c} onOpenLinked={setLinked} /> : <GenericBody card={c} type={types.data?.items.find((t) => t.key === c.type)} />}

      {notes && (
        <section class="section" aria-labelledby="notes-h">
          <h2 id="notes-h">{c.type === 'recipe' ? 'Notes' : 'Content'}</h2>
          <div class="prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(notes) }} />
        </section>
      )}

      {c.media.filter((m) => m.role !== 'cover').length > 0 && (
        <section class="section">
          <h2>Photos</h2>
          <div class="grid compact">
            {c.media
              .filter((m) => m.role !== 'cover')
              .map((m) => (
                <a href={m.url} target="_blank" rel="noopener">
                  <img src={m.thumb_url} alt={m.alt_text ?? ''} loading="lazy" style={{ borderRadius: 'var(--radius-md)' }} />
                </a>
              ))}
          </div>
        </section>
      )}

      {cooked.length > 0 && (
        <section class="section" aria-labelledby="cooked-h">
          <h2 id="cooked-h">Baked log</h2>
          <ul class="ing-list">
            {[...cooked].reverse().map((e) => (
              <li>
                <span class="ing-text">
                  <strong>{e.date}</strong> {e.rating ? <Stars value={e.rating} /> : null} {e.note && <span class="muted">— {e.note}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(c.links.length > 0 || (c.collections?.length ?? 0) > 0) && (
        <section class="section">
          <h2>Related</h2>
          <div class="chips">
            {c.links.map((l) => (
              <Link class="dr-tag" href={`/c/${l.id}`}>
                {l.direction === 'in' ? (l.relation === 'uses' ? 'used by' : l.relation.replace('_', ' ')) : l.relation.replace('_', ' ')}: {l.title}
              </Link>
            ))}
            {c.collections?.map((co) => (
              <Link class="dr-tag" href={`/collections/${co.id}`}>
                ❏ {co.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <p class="muted small">
        {admin ? (
          <>
            Added {new Date(c.created_at).toLocaleDateString()} by {c.created_by === 'user' ? 'you' : c.created_by.replace('client:', '')} · updated{' '}
            {new Date(c.updated_at).toLocaleString()}
          </>
        ) : (
          <>Updated {new Date(c.updated_at).toLocaleDateString()}</>
        )}
      </p>

      <button type="button" class="dr-next bk-next-btn" onClick={() => void shuffle()}>
        <span class="dr-label">Next frame</span>
        <span class="dr-section-title">
          Show me another recipe <span class="dr-stop">→</span>
        </span>
      </button>

      {linked && <SubRecipeSheet id={linked} onClose={() => setLinked(null)} />}
      {dialog === 'cooked' && (
        <CookedDialog
          card={c}
          onClose={() => setDialog(null)}
          onSaved={(n) => {
            setData({ ...n, collections: c.collections });
            setDialog(null);
          }}
        />
      )}
      {dialog === 'collection' && <CollectionDialog card={c} onClose={() => setDialog(null)} />}
    </article>
  );
}
